import moment from './moment-timezone';
import cookies from 'react-cookies';
import { Store } from 'react-notifications-component';
import { API_ENDPOINT, API_URL } from '../api/constant';
import { callApi } from '../api/callApi';

/**
 * common function to set cookies in react application
 * @param name name of cookie
 * @param value value to set in cookie
 */
export const setCookies = (name: string, value: any) => {
    cookies.save(name, value, { path: import.meta.env.VITE_PUBLIC_URL });
};

/**
 * 
 * @param name find cookies by name
 * @returns 
 */
export const getCookies = (name: string) => {
    return cookies.load(name);
};

/**
 * 
 * @param name Delete cookies by name
 * @returns 
 */
export const deleteCookies = (name: string) => {
    cookies.remove(name, { path: import.meta.env.VITE_PUBLIC_URL });
};

/**
 * 
 * @returns reutn the saved auth token
 */
export const getToken = () => {
    return cookies.load(import.meta.env.VITE_BUSINESS + 'token');
};

/**
 * delete the browser data when user logs out 
 */
export const logoutUser = async () => {
    try {
        await callApi(API_URL + API_ENDPOINT.auth + `user/logout?deviceId=${getDeviceId()}&logoutAll=true`, {
            method: "DELETE",
        });
        deleteCookies(import.meta.env.VITE_BUSINESS + "token");
        deleteCookies(import.meta.env.VITE_BUSINESS + "refreshToken");
        deleteCookies(import.meta.env.VITE_BUSINESS + "deviceToken",);
        localStorage.clear();
        setTimeout(() => {
            window.location.href = import.meta.env.VITE_PUBLIC_URL ? import.meta.env.VITE_PUBLIC_URL : '/';
        }, 1500);
    } catch (error) {
        deleteCookies(import.meta.env.VITE_BUSINESS + "token");
        deleteCookies(import.meta.env.VITE_BUSINESS + "refreshToken");
        deleteCookies(import.meta.env.VITE_BUSINESS + "deviceToken",);
        localStorage.clear();
        setTimeout(() => {
            window.location.href = import.meta.env.VITE_PUBLIC_URL ? import.meta.env.VITE_PUBLIC_URL : '/';
        }, 1500);
        console.log("Error in logout", error);
    }

};


/**
 * 
 * @param type type of toast notification
 * @param message message to show in toast notification
 */
export const showToastNotification = (type: 'success' | 'danger' | 'info' | 'default' | 'warning', message: string) => {
    Store.addNotification({
        message: message,
        type: type,
        insert: 'top',
        container: 'top-right',
        animationIn: ['animate__animated', 'animate__fadeIn'],
        animationOut: ['animate__animated', 'animate__fadeOut'],
        dismiss: {
            duration: 5000,
            onScreen: true,
        },
    });
};


export function toFixedThree(input: string | number) {
    return parseFloat(input.toString()).toFixed(3)
}

/**
 * Round a money value to the currency precision used across the app (BHD, 3 decimals).
 * Keeps repeated additions/allocations free of floating point drift.
 */
export function roundCurrency(input: string | number): number {
    const value = parseFloat((input ?? 0).toString());
    if (!Number.isFinite(value)) return 0;
    return Math.round((value + Number.EPSILON) * 1000) / 1000;
}


export const public_url = import.meta.env.VITE_PUBLIC_URL || ''


export const pageLinks = [
    { label: 'Homepage', value: 'home' },
    { label: 'Products', value: 'products' },
    { label: 'Recipes', value: 'recipes' },
    { label: 'Our Brand', value: 'our-brand' },
    { label: 'Shop Online', value: 'shop-online' },
    { label: 'Recipe Detail', value: 'recipe-detail' }
]

export const bannerPageLink = [
    { label: 'Homepage', value: 'home' },
    { label: 'Our Brand', value: 'our-brand' },
    { label: 'Subscriptions', value: 'subscriptions' },
    { label: 'Offers', value: 'offers' },
    { label: 'The Meatery Box', value: 'the-meatery-box' }
]


export const CountryArray = [
    { label: 'USA', value: 'USA' },
    { label: 'NEW ZEALAND', value: 'NEW ZEALAND' },
    { label: 'HOLLAND', value: 'HOLLAND' },
]

export const getTimeDifference = (
    status1: string,
    status2: string,
    data: { status: string; dateTime: string }[]
): string => {
    const time1 = new Date(data.find(d => d.status === status1)?.dateTime || "");
    const time2 = new Date(data.find(d => d.status === status2)?.dateTime || "");

    if (isNaN(time1.getTime()) || isNaN(time2.getTime())) {
        return `N/A`;
    }

    const diffMs = time2.getTime() - time1.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);
    const diffSeconds = Math.floor((diffMs % 60000) / 1000);

    return `${diffMinutes} minutes and ${diffSeconds} seconds.`;
};


export function formatDateTime(input: string): string {
    if (input) {

        const [datePart, timePart] = input.split('_');
        const formattedDate = moment(datePart, "YYYY-MM-DD").format("DD MMM, YYYY");
        return `${formattedDate} ${timePart}`;
    } else {
        return 'N/A'
    }
}



let cachedDeviceId: string | null = null;

export function getDeviceId(): string {
    // Return the cached device ID if available
    if (cachedDeviceId !== null) {
        return cachedDeviceId;
    }

    // Attempt to retrieve an existing device ID from localStorage
    let deviceId = localStorage.getItem('deviceId');
    if (!deviceId) {
        // Use crypto.randomUUID() if available for secure UUID generation
        if (window.crypto && typeof crypto.randomUUID === 'function') {
            deviceId = crypto.randomUUID();
        } else {
            // Fallback: simple UUID generator
            deviceId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c: string): string => {
                const r = Math.random() * 16 | 0;
                const v = c === 'x' ? r : (r & 0x3 | 0x8);
                return v.toString(16);
            });
        }
        // Save the generated ID so that it persists across sessions
        localStorage.setItem('deviceId', deviceId);
    }

    // Cache the ID in memory for subsequent call
    cachedDeviceId = deviceId;
    return deviceId;
}

export const getPaymentMethodMessage = (method: string): string => {
    const paymentMethodMap: Record<string, string> = {
        cod: "Cash on Delivery",
        wallet: "Paid via Wallet",
        card: "Paid via Credit Card",
        apple_pay: "Paid via Apple Pay",
        benefitpay: "Paid via BenefitPay",
        "bh.benefit": "Paid via Debit Card",
    };

    const key = method.toLowerCase();
    return paymentMethodMap[key] || "Unknown Payment Method";
}



export const generateColors = (count: number): string[] => {
    const colors: string[] = [];
    for (let i = 0; i < count; i++) {
        const hue = (i * 360) / count;
        colors.push(`hsla(${hue}, 70%, 60%, 0.5)`);
    }

    return colors;
};